<template>
  <div class="view-container">
    <div class="main-layout">
      <div class="left-panel">
        <div class="panel-toolbar">
          <div class="search-box">
            <i class="fas fa-search search-icon"></i>
            <input v-model="treeSearch" type="text" placeholder="搜索组织..." class="search-input" @input="onSearch">
          </div>
          <button @click="openAddDialog(0)" class="btn btn-add">
            <i class="fas fa-plus"></i> 新增
          </button>
        </div>
        <div class="panel-body">
          <table class="org-table">
            <thead>
              <tr>
                <th>组织名称</th>
                <th width="80">操作</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="node in filteredTree" :key="node.id">
                <OrgRow
                  :node="node"
                  :depth="0"
                  :selected-id="selectedOrgId"
                  :expanded-ids="expandedIds"
                  :all-orgs="organizations"
                  @select="selectOrg"
                  @edit="openEditDialog"
                  @delete="confirmDelete"
                  @add-child="openAddDialog"
                  @toggle-expand="toggleExpand"
                />
              </template>
              <tr v-if="filteredTree.length === 0">
                <td colspan="2" class="empty-row">
                  {{ treeSearch ? '未找到匹配的组织' : '暂无组织，点击上方按钮新建' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="right-panel">
        <div class="panel-body">
          <div v-if="selectedOrgId && selectedOrg">
            <div v-if="currentMembers.length > 0">
              <table class="member-table">
                <thead>
                  <tr>
                    <th width="40">#</th>
                    <th>姓名</th>
                    <th>账号</th>
                    <th>职位</th>
                    <th>职级</th>
                    <th width="60">负责人</th>
                    <th width="60">操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(member, idx) in currentMembers" :key="member.id">
                    <td>{{ idx + 1 }}</td>
                    <td>{{ member.name }}</td>
                    <td>{{ member.account }}</td>
                    <td><input v-model="member.position" type="text" class="small-input" placeholder="职位" @input="onMemberChange"></td>
                    <td><input v-model="member.rank" type="text" class="small-input" placeholder="职级" @input="onMemberChange"></td>
                    <td>
                      <label class="leader-switch">
                        <input v-model="member.isLeader" type="checkbox" @change="onMemberChange">
                        <span>{{ member.isLeader ? '是' : '否' }}</span>
                      </label>
                    </td>
                    <td>
                      <button @click="removeSingleMember(member.id)" class="btn-remove">
                        <i class="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="no-members">
              <p>该组织暂无成员</p>
              <button @click="openMemberAdd" class="btn-add-member-sm">
                <i class="fas fa-plus"></i> 添加成员
              </button>
            </div>
            <div class="member-footer" v-if="currentMembers.length > 0">
              <span v-if="memberChanged" class="changed-indicator"></span>
              <span v-if="memberChanged">有未保存的更改</span>
              <button @click="saveMembers" class="btn-save" :disabled="!memberChanged">
                <i class="fas fa-save"></i> 保存
              </button>
            </div>
          </div>
          <div v-else class="empty-state">
            <i class="fas fa-users"></i>
            <p>请选择一个组织</p>
            <p class="hint">在左侧点击组织名称查看成员信息</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <div v-if="showFormModal" class="modal-overlay" @click.self="closeFormModal">
      <div class="modal-dialog">
        <div class="modal-header">
          <h4>{{ formTitle }}</h4>
          <button @click="closeFormModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>序号 (Id) *</label>
            <input v-model.number="form.no" type="number" class="form-control" placeholder="请输入序号">
          </div>
          <div class="form-group">
            <label>组织名称 *</label>
            <input v-model="form.name" type="text" class="form-control" placeholder="请输入组织名称" required>
          </div>
          <div class="form-group">
            <label>组织编码</label>
            <input v-model="form.code" type="text" class="form-control" placeholder="请输入组织编码">
          </div>
          <div class="form-group">
            <label>上级组织</label>
            <select v-model="form.parentId" class="form-control">
              <option :value="0">无（顶级组织）</option>
              <option v-for="o in availableParents" :key="o.id" :value="o.id">{{ o.name }}</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeFormModal" class="btn btn-default">取消</button>
          <button @click="saveForm" class="btn btn-primary">确认保存</button>
        </div>
      </div>
    </div>

    <!-- 添加成员弹窗 -->
    <div v-if="showMemberAddModal" class="modal-overlay" @click.self="closeMemberAddModal">
      <div class="modal-dialog member-dialog">
        <div class="modal-header">
          <h4>添加成员</h4>
          <button @click="closeMemberAddModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="search-box-inner">
            <input v-model="memberAddSearch" type="text" placeholder="搜索用户...">
          </div>
          <div class="member-select-list">
            <label
              v-for="user in availableUsers"
              :key="user.id"
              class="select-item"
            >
              <input
                type="checkbox"
                :checked="pendingMemberIds.includes(user.id)"
                @change="togglePendingMember(user.id)"
              >
              <span>{{ user.name }} ({{ user.account }})</span>
            </label>
            <div v-if="availableUsers.length === 0" class="empty-tip">没有可添加的用户</div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeMemberAddModal" class="btn btn-default">取消</button>
          <button @click="confirmAddMembers" class="btn btn-primary" :disabled="pendingMemberIds.length === 0">
            添加 ({{ pendingMemberIds.length }})
          </button>
        </div>
      </div>
    </div>

    <!-- 通用确认弹窗（删除组织） -->
    <ConfirmModal
      :visible="showConfirm"
      :title="confirmTitle"
      :message="confirmMessage"
      confirmText="确认删除"
      type="danger"
      :loading="confirmLoading"
      @confirm="onConfirmDelete"
      @cancel="showConfirm = false"
    />

    <!-- 成员移除确认弹窗 -->
    <ConfirmModal
      :visible="showMemberConfirm"
      :title="memberConfirmTitle"
      :message="memberConfirmMessage"
      confirmText="确认移除"
      type="warning"
      :loading="memberConfirmLoading"
      @confirm="doRemoveMember"
      @cancel="showMemberConfirm = false"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, h, onMounted, watch } from 'vue'
import { getOrgTree, getOrgUsers, saveOrg as saveOrgApi, deleteOrg as deleteOrgApi, addOrgUser, removeOrgUser } from '../api/org'
import { getUserList } from '../api/user'
import ConfirmModal from '../components/ConfirmModal.vue'

const organizations = ref([])
const allUsers = ref([])

// 后端 OrgTreeNode -> 视图节点结构
const mapNode = (n) => ({
  id: n.guid || n.Guid,
  name: n.ousName || n.oUsName || n.OUsName || n.name || n.Name || '',
  code: n.ousCode || n.oUsCode || n.OUsCode || '',
  parentId: n.superGuid || n.SuperGuid || 0,
  no: n.no ?? n.No ?? 0,
  children: (n.children || n.Children || []).map(mapNode),
  members: [],
  _loaded: false
})

// 递归获取所有节点ID（用于默认展开）
const getAllNodeIds = (nodes) => {
  let ids = []
  const collect = (list) => {
    list.forEach(node => {
      ids.push(node.id)
      if (node.children && node.children.length > 0) {
        collect(node.children)
      }
    })
  }
  collect(nodes)
  return ids
}

const loadTree = async () => {
  try {
    const res = await getOrgTree(treeSearch.value || undefined)
    const list = res.data || res.Data || []
    organizations.value = list.map(mapNode)
    // 默认展开所有节点
    expandedIds.value = getAllNodeIds(organizations.value)
  } catch (e) {
    console.error('加载组织树失败', e)
  }
}

const loadUsers = async () => {
  try {
    const res = await getUserList()
    allUsers.value = (res.data || res.Data || []).map(u => ({
      id: u.account || u.Account,
      name: u.displayName || u.DisplayName,
      account: u.account || u.Account,
      guid: u.guid || u.Guid
    }))
  } catch (e) {
    console.error('加载用户失败', e)
  }
}

onMounted(() => { loadTree(); loadUsers() })

const treeSearch = ref('')
const selectedOrgId = ref(null)
const selectedMemberIds = ref([])
const memberChanged = ref(false)
const showFormModal = ref(false)
const showMemberAddModal = ref(false)
const memberAddSearch = ref('')
const pendingMemberIds = ref([])
const expandedIds = ref([])
const form = reactive({ no: 0, name: '', code: '', parentId: 0, id: null })

// 确认弹窗（删除组织）
const showConfirm = ref(false)
const confirmLoading = ref(false)
const confirmTitle = ref('')
const confirmMessage = ref('')
const pendingDeleteNode = ref(null)

// 确认弹窗（移除成员）
const showMemberConfirm = ref(false)
const memberConfirmLoading = ref(false)
const memberConfirmTitle = ref('')
const memberConfirmMessage = ref('')
let pendingMemberRemoveIds = []

const onSearch = () => {
  loadTree()
}

const selectedOrg = computed(() => {
  const findOrg = (list, id) => {
    for (const org of list) {
      if (org.id === id) return org
      if (org.children) {
        const found = findOrg(org.children, id)
        if (found) return found
      }
    }
    return null
  }
  return findOrg(organizations.value, selectedOrgId.value)
})

const currentMembers = computed(() => selectedOrg.value?.members || [])

const filteredTree = computed(() => {
  // 搜索已在后端完成，直接返回
  return organizations.value
})

const availableParents = computed(() => {
  const excludeId = form.id
  const list = []
  const collect = (orgs, prefix = '') => {
    orgs.forEach(org => {
      if (org.id !== excludeId) {
        list.push({ id: org.id, name: prefix ? `${prefix} / ${org.name}` : org.name })
      }
      if (org.children) collect(org.children, prefix ? `${prefix} / ${org.name}` : org.name)
    })
  }
  collect(organizations.value)
  return list
})

const formTitle = computed(() => form.id ? '修改组织' : '新增组织')

const availableUsers = computed(() => {
  const existingAccounts = currentMembers.value.map(m => m.account)
  return allUsers.value.filter(u => !existingAccounts.includes(u.account)).filter(u =>
    !memberAddSearch.value || u.name.toLowerCase().includes(memberAddSearch.value.toLowerCase()) ||
    u.account.toLowerCase().includes(memberAddSearch.value.toLowerCase())
  )
})

function toggleExpand(id) {
  const idx = expandedIds.value.indexOf(id)
  if (idx !== -1) expandedIds.value.splice(idx, 1)
  else expandedIds.value.push(id)
}

async function selectOrg(node) {
  if (memberChanged.value) {
    if (!await $confirm('成员信息尚未保存，确定切换吗？')) return
  }
  selectedOrgId.value = node.id
  selectedMemberIds.value = []
  memberChanged.value = false
  try {
    const res = await getOrgUsers(node.id)
    const members = (res.data || res.Data || []).map(m => ({
      id: m.account || m.Account,
      name: m.displayName || m.DisplayName,
      account: m.account || m.Account,
      position: m.jobTitle || m.JobTitle || '',
      rank: '',
      isLeader: (m.memberType ?? m.MemberType) === 1
    }))
    if (node) node.members = members
  } catch (e) {
    console.error('加载组织成员失败', e)
  }
}

function openAddDialog(parentId) {
  form.no = 0
  form.name = ''
  form.code = ''
  form.parentId = parentId || 0
  form.id = null
  showFormModal.value = true
}

function openEditDialog(node) {
  form.no = node.no || 0
  form.name = node.name
  form.code = node.code || ''
  form.parentId = node.parentId || 0
  form.id = node.id
  showFormModal.value = true
}

function confirmDelete(node) {
  pendingDeleteNode.value = node
  confirmTitle.value = '确认删除组织'
  confirmMessage.value = `确定要删除组织「${node.name}」吗？删除后该组织及其子组织的关联将被移除。`
  showConfirm.value = true
}

async function onConfirmDelete() {
  if (!pendingDeleteNode.value) return
  confirmLoading.value = true
  try {
    await deleteOrgApi(pendingDeleteNode.value.id)
    if (selectedOrgId.value === pendingDeleteNode.value.id) selectedOrgId.value = null
    showConfirm.value = false
    await loadTree()
  } catch (e) {
    $message.error('删除失败：' + e.message)
  } finally {
    confirmLoading.value = false
  }
}

function closeFormModal() {
  showFormModal.value = false
  form.no = 0
  form.name = ''
  form.code = ''
  form.parentId = 0
  form.id = null
}

async function saveForm() {
  if (!form.name.trim()) {
    $message.warning('请输入组织名称')
    return
  }
  const payload = {
    Guid: form.id || '',
    OUsName: form.name,
    OUsCode: form.code || '',
    SuperGuid: (form.parentId && form.parentId !== 0) ? form.parentId : '',
    No: form.no || 0,
    Active: 1
  }
  try {
    await saveOrgApi(payload)
    closeFormModal()
    await loadTree()
  } catch (e) {
    $message.error('保存失败：' + e.message)
  }
}

function openMemberAdd() {
  memberAddSearch.value = ''
  pendingMemberIds.value = []
  showMemberAddModal.value = true
}

function closeMemberAddModal() {
  showMemberAddModal.value = false
}

function togglePendingMember(id) {
  const idx = pendingMemberIds.value.indexOf(id)
  if (idx !== -1) pendingMemberIds.value.splice(idx, 1)
  else pendingMemberIds.value.push(id)
}

async function confirmAddMembers() {
  if (!selectedOrg.value) return
  const orgGuid = selectedOrg.value.id
  try {
    for (const id of pendingMemberIds.value) {
      const user = availableUsers.value.find(u => u.id === id)
      if (user) {
        await addOrgUser(orgGuid, { Guid: user.guid, Account: user.account, DisplayName: user.name })
      }
    }
    closeMemberAddModal()
    await selectOrg(selectedOrg.value)
  } catch (e) {
    $message.error('添加成员失败：' + e.message)
  }
}

function toggleMemberSelect(id) {
  const idx = selectedMemberIds.value.indexOf(id)
  if (idx !== -1) selectedMemberIds.value.splice(idx, 1)
  else selectedMemberIds.value.push(id)
}

function onMemberChange() {
  memberChanged.value = true
}

async function removeSingleMember(memberId) {
  if (!selectedOrg.value) return
  const member = currentMembers.value.find(m => m.id === memberId)
  if (!member) return

  memberConfirmTitle.value = '确认移除成员'
  memberConfirmMessage.value = `确定要移除成员「${member.name}(${member.account})」吗？`
  pendingMemberRemoveIds = [memberId]
  showMemberConfirm.value = true
}

async function doRemoveMember() {
  if (!selectedOrg.value || pendingMemberRemoveIds.length === 0) return
  memberConfirmLoading.value = true
  try {
    const targets = currentMembers.value.filter(m => pendingMemberRemoveIds.includes(m.id))
    for (const m of targets) {
      await removeOrgUser(selectedOrg.value.id, m.account)
    }
    selectedMemberIds.value = selectedMemberIds.value.filter(id => !pendingMemberRemoveIds.includes(id))
    showMemberConfirm.value = false
    await selectOrg(selectedOrg.value)
  } catch (e) {
    $message.error('移除失败：' + (e.response?.data?.message || e.message))
  } finally {
    memberConfirmLoading.value = false
  }
}

async function saveMembers() {
  $message.info('Save members endpoint not implemented in backend')
}

// 递归组织行组件
const OrgRow = {
  name: 'OrgRow',
  props: ['node', 'depth', 'selectedId', 'allOrgs', 'expandedIds'],
  emits: ['select', 'edit', 'delete', 'add-child', 'toggle-expand'],
  setup(props, { emit }) {
    return () => {
      const { node, depth, selectedId, expandedIds } = props
      const hasChildren = node.children && node.children.length > 0
      const isExpanded = expandedIds.includes(node.id)

      const children = []
      children.push(
        h('tr', {
          class: ['org-row', { selected: selectedId === node.id }],
          onClick: () => emit('select', node)
        }, [
          h('td', { style: { paddingLeft: (depth * 24 + 8) + 'px' } }, [
            h('span', { class: 'org-name-cell' }, [
              hasChildren
                ? h('span', {
                    class: ['expand-icon', { expanded: isExpanded }],
                    onClick: (e) => { e.stopPropagation(); emit('toggle-expand', node.id) }
                  }, h('i', { class: 'fas fa-caret-right' }))
                : h('span', { class: 'expand-placeholder' }),
              h('i', { class: ['fas', hasChildren ? 'fa-folder' : 'fa-folder-open'] }),
              h('span', { class: 'org-name' }, node.name)
            ])
          ]),
          h('td', { class: 'action-cell' }, [
            h('button', {
              class: 'action-btn add',
              title: '添加子组织',
              onClick: (e) => { e.stopPropagation(); emit('add-child', node.id) }
            }, h('i', { class: 'fas fa-plus' })),
            h('button', {
              class: 'action-btn edit',
              title: '编辑',
              onClick: (e) => { e.stopPropagation(); emit('edit', node) }
            }, h('i', { class: 'fas fa-edit' })),
            h('button', {
              class: 'action-btn delete',
              title: '删除',
              onClick: (e) => { e.stopPropagation(); emit('delete', node) }
            }, h('i', { class: 'fas fa-trash' }))
          ])
        ])
      )

      if (hasChildren && isExpanded) {
        node.children.forEach(child => {
          children.push(h(OrgRow, {
            key: child.id,
            node: child,
            depth: depth + 1,
            selectedId: selectedId,
            allOrgs: props.allOrgs,
            expandedIds: expandedIds,
            onSelect: (n) => emit('select', n),
            onEdit: (n) => emit('edit', n),
            onDelete: (n) => emit('delete', n),
            onAddChild: (id) => emit('add-child', id),
            onToggleExpand: (id) => emit('toggle-expand', id)
          }))
        })
      }

      return children
    }
  }
}
</script>

<style scoped>
.view-container {
  padding: 16px;
  background: #f5f7fa;
  height: calc(100vh - 150px);
  box-sizing: border-box;
  overflow: hidden;
}

.main-layout {
  display: flex;
  gap: 16px;
  height: 100%;
}

.left-panel {
  flex: 0 0 22%;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e8ecf1;
  display: flex;
  flex-direction: column;
}

.right-panel {
  flex: 1;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e8ecf1;
  display: flex;
  flex-direction: column;
}

.panel-toolbar {
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #e8ecf1;
  background: #fafbfc;
}

.panel-body {
  flex: 1;
  padding: 8px;
  overflow-y: auto;
}

.search-box {
  position: relative;
  flex: 1;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 12px;
}

.search-input {
  width: 100%;
  padding: 6px 10px 6px 28px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  transition: border-color 0.15s;
}
.search-input:focus { border-color: #1ab394; }

.btn-add {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #1ab394;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}
.btn-add:hover { background: #17a689; }

.org-table {
  width: 100%;
  border-collapse: collapse;
}

.org-table thead th {
  padding: 8px 10px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  background: #f8fafc;
  border-bottom: 1px solid #e8ecf1;
}

.org-table tbody td {
  padding: 6px 10px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 12px;
  color: #374151;
}

.org-row {
  cursor: pointer;
  transition: background 0.1s;
}

.org-row:hover { background: #f9fafb; }

.org-row.selected {
  background: #ecfdf5;
}

.org-row.selected td {
  color: #059669;
}

.empty-row {
  text-align: center;
  padding: 20px;
  color: #9ca3af;
}

.org-name-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.org-name-cell i {
  color: #0ea5e9;
  font-size: 12px;
}

.org-name { font-size: 13px; font-weight: 500; }

.expand-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  cursor: pointer;
  transition: transform 0.2s;
  font-size: 12px;
  color: #9ca3af;
}

.expand-icon.expanded { transform: rotate(90deg); }

.expand-placeholder { display: inline-block; width: 16px; }

.action-cell { display: flex; gap: 4px; justify-content: flex-end; }

.action-btn {
  width: 22px;
  height: 22px;
  border: 1px solid #e5e7eb;
  border-radius: 3px;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  transition: all 0.15s;
}

.action-btn.add { color: #10b981; border-color: #d1fae5; }
.action-btn.add:hover { background: #10b981; color: #fff; border-color: #10b981; }

.action-btn.edit { color: #6366f1; border-color: #e0e7ff; }
.action-btn.edit:hover { background: #6366f1; color: #fff; border-color: #6366f1; }

.action-btn.delete { color: #ef4444; border-color: #fee2e2; }
.action-btn.delete:hover { background: #ef4444; color: #fff; border-color: #ef4444; }

.member-footer {
  padding: 10px 16px;
  border-top: 1px solid #e8ecf1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.changed-indicator {
  width: 8px;
  height: 8px;
  background: #f59e0b;
  border-radius: 50%;
  display: inline-block;
}

.btn-save {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  background: #10b981;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-size: 11px;
  cursor: pointer;
}
.btn-save:hover { background: #059669; }
.btn-save:disabled { background: #a7f3d0; cursor: not-allowed; }

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
}

.empty-state i { font-size: 32px; margin-bottom: 12px; opacity: 0.5; }
.empty-state p { margin: 4px 0; font-size: 13px; }
.empty-state .hint { font-size: 11px; color: #d1d5db; }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-dialog {
  background: #fff;
  border-radius: 6px;
  width: 400px;
  max-width: 90vw;
}
.modal-dialog.member-dialog { width: 480px; }

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-bottom: 1px solid #e8ecf1;
}
.modal-header h4 { margin: 0; font-size: 13px; font-weight: 600; color: #1f2937; }

.modal-close {
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  font-size: 18px;
  color: #9ca3af;
  cursor: pointer;
}
.modal-close:hover { color: #374151; }

.modal-body { padding: 14px; }

.form-group { margin-bottom: 12px; }
.form-group label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
}

.form-control {
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.form-control:focus { border-color: #1ab394; }

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid #e8ecf1;
}

.search-box-inner { margin-bottom: 10px; }
.search-box-inner input {
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  box-sizing: border-box;
}
.search-box-inner input:focus { border-color: #1ab394; }

.member-select-list { max-height: 240px; overflow-y: auto; }

.select-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  cursor: pointer;
  font-size: 12px;
  border-radius: 3px;
}
.select-item:hover { background: #f9fafb; }
.select-item input { accent-color: #10b981; width: 14px; height: 14px; }

.empty-tip {
  text-align: center;
  padding: 16px;
  color: #9ca3af;
  font-size: 12px;
}

.member-table { width: 100%; border-collapse: collapse; }

.member-table thead th {
  padding: 8px 10px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  background: #f8fafc;
  border-bottom: 1px solid #e8ecf1;
}

.member-table tbody td {
  padding: 8px 10px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 12px;
  color: #374151;
}

.small-input {
  width: 100%;
  padding: 4px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 3px;
  font-size: 12px;
  box-sizing: border-box;
}
.small-input:focus { border-color: #1ab394; outline: none; }

.leader-switch {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-size: 12px;
}
.leader-switch input { accent-color: #10b981; width: 13px; height: 13px; }

.btn-remove {
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 3px;
  background: #fee2e2;
  color: #ef4444;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
}
.btn-remove:hover { background: #ef4444; color: #fff; }

.no-members {
  text-align: center;
  padding: 24px;
  color: #9ca3af;
}
.no-members p { margin: 0 0 10px; font-size: 13px; }

.btn-add-member-sm {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 5px 10px;
  background: #10b981;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-size: 11px;
  cursor: pointer;
}

.btn { font-size: 12px; padding: 6px 14px; border-radius: 4px; cursor: pointer; border: 1px solid transparent; }
.btn-default { background: #f3f4f6; border-color: #d1d5db; color: #374151; }
.btn-default:hover { background: #e5e7eb; }
.btn-primary { background: #1ab394; border-color: #1ab394; color: #fff; }
.btn-primary:hover { background: #17a689; }
</style>

<style>
/* 非 scoped 样式 — OrgRow render 函数组件不受 scoped CSS 影响 */
.org-table .org-row { cursor: pointer; transition: background 0.1s; }
.org-table .org-row:hover { background: #f9fafb; }
.org-table .org-row.selected { background: #ecfdf5; }
.org-table .org-row.selected td { color: #059669; }
.org-table .empty-row { text-align: center; padding: 20px; color: #9ca3af; }

.org-table .org-name-cell { display: flex; align-items: center; gap: 6px; }
.org-table .org-name-cell i { color: #10b981; font-size: 12px; }
.org-table .org-name { font-size: 13px; font-weight: 500; }

.org-table .expand-icon { display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; cursor: pointer; transition: transform 0.2s; font-size: 12px; color: #9ca3af; }
.org-table .expand-icon.expanded { transform: rotate(90deg); }
.org-table .expand-placeholder { display: inline-block; width: 16px; }

.org-table .action-cell { display: flex; gap: 4px; justify-content: flex-end; }
.org-table .action-btn { width: 22px; height: 22px; border: 1px solid #e5e7eb; border-radius: 3px; background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 10px; transition: all 0.15s; }
.org-table .action-btn.add { color: #10b981; border-color: #d1fae5; }
.org-table .action-btn.add:hover { background: #10b981; color: #fff; border-color: #10b981; }
.org-table .action-btn.edit { color: #6366f1; border-color: #e0e7ff; }
.org-table .action-btn.edit:hover { background: #6366f1; color: #fff; border-color: #6366f1; }
.org-table .action-btn.delete { color: #ef4444; border-color: #fee2e2; }
.org-table .action-btn.delete:hover { background: #ef4444; color: #fff; border-color: #ef4444; }
</style>
